import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Archive, Check, Plus, RotateCcw, SearchCheck, Sparkles } from 'lucide-react';

import { listEmployees, type EmployeeListItem } from '@/api/employees.api';
import {
  addStarterTaxonomy,
  createTaxonomyItem,
  getInvestigationConfig,
  listInvestigationTaxonomy,
  updateInvestigationConfig,
  updateTaxonomyItem,
  type EscalationStrategy,
  type InvestigationConfig,
  type TaxonomyBundle,
  type TaxonomyItem,
  type TaxonomyKind,
} from '@/api/investigations.api';
import type { ChainStep } from '@/api/sops.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Tabs } from '@/components/ui/Tabs';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { hasCapability } from '@/lib/capabilities';

/**
 * Investigations settings (ADR-049) — `manage_investigations` holders.
 *
 * Three tabs:
 *   - Rules     — the rigor toggles (snapshotted onto each NEW record) and the
 *                 allowed resolution actions
 *   - Routing   — investigator pool, escalation strategy, who quality-reviews,
 *                 who is told about an unassigned record
 *   - Taxonomy  — issue types, causes, stations, inconclusive reasons. Items
 *                 retire, never delete: closed records point at them.
 *
 * The server validates the MERGED config, so each tab sends only its own
 * fields and cannot clobber the others.
 */

type Tab = 'rules' | 'routing' | 'taxonomy';

const TABS: { id: Tab; label: string }[] = [
  { id: 'rules', label: 'Rules' },
  { id: 'routing', label: 'Routing' },
  { id: 'taxonomy', label: 'Taxonomy' },
];

type RigorKey =
  | 'requireQualityReview'
  | 'requireSignedClosure'
  | 'requireReasonForChange'
  | 'requireStationAttribution'
  | 'requireCorrectiveAction'
  | 'requireInconclusiveReason'
  | 'requireIndependentReviewer'
  | 'allowPersonAttribution';

const RIGOR_TOGGLES: { key: RigorKey; label: string; description: string }[] = [
  {
    key: 'requireQualityReview',
    label: 'Quality review before closure',
    description: 'A resolved record goes to the quality reviewer, who accepts it (closing) or returns it.',
  },
  {
    key: 'requireSignedClosure',
    label: 'Signed closure',
    description: 'Closing requires a typed-name electronic signature (21 CFR Part 11).',
  },
  {
    key: 'requireIndependentReviewer',
    label: 'Independent reviewer',
    description: 'The quality reviewer may not be the investigator.',
  },
  {
    key: 'requireReasonForChange',
    label: 'Reason for every change',
    description: 'Edits to a record must state why (cGMP §111.180(c)).',
  },
  {
    key: 'requireStationAttribution',
    label: 'Station required to close',
    description: 'Where in the process it originated must be recorded before closing.',
  },
  {
    key: 'requireCorrectiveAction',
    label: 'Corrective action required to close',
    description: 'A pick from the effectiveness hierarchy — “none” is an explicit choice.',
  },
  {
    key: 'requireInconclusiveReason',
    label: 'Inconclusive needs a reason',
    description: 'An inconclusive record must say why the evidence ran out.',
  },
  {
    key: 'allowPersonAttribution',
    label: 'Allow person attribution',
    description:
      'A named person may be recorded, visible only to holders of “View investigation attribution”. Off = no such record ever exists.',
  },
];

const ROLE_OPTIONS = [
  'visionary',
  'integrator',
  'department_lead',
  'employee',
  'quality_lead',
  'qa_personnel',
  'auditor_readonly',
] as const;

const SELECT_CLASS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const TAXONOMY_SECTIONS: {
  kind: TaxonomyKind;
  bundleKey: keyof TaxonomyBundle;
  title: string;
  hint: string;
}[] = [
  {
    kind: 'issue_types',
    bundleKey: 'issueTypes',
    title: 'Issue types',
    hint: 'What went wrong, as the initiator sees it — the first pick on every record.',
  },
  {
    kind: 'causes',
    bundleKey: 'causes',
    title: 'Causes',
    hint: 'Why it went wrong. The category groups causes on the weekly report.',
  },
  {
    kind: 'stations',
    bundleKey: 'stations',
    title: 'Stations',
    hint: 'Where in the process it originated — the attribution everyone can see.',
  },
  {
    kind: 'inconclusive_reasons',
    bundleKey: 'inconclusiveReasons',
    title: 'Inconclusive reasons',
    hint: 'Why the evidence ran out. Counting these is how evidence gaps get fixed.',
  },
];

export function InvestigationsSettingsPage() {
  const { state: authState } = useAuth();
  const [tab, setTab] = useState<Tab>('rules');

  const canManage =
    authState.status === 'authenticated' && hasCapability(authState.me, 'manage_investigations');

  const configQuery = useQuery({
    queryKey: ['investigations', 'config'],
    queryFn: getInvestigationConfig,
    enabled: canManage,
  });
  const employeesQuery = useQuery({
    queryKey: ['employees', 'all-for-investigations'],
    queryFn: () => listEmployees({ status: 'all', limit: 500 }),
    enabled: canManage,
  });

  if (!canManage) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card className="flex items-start gap-3 p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-warning-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-shark">Investigations admin access required</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              This page needs the “Manage investigations” capability. Visionary and integrator roles hold it
              implicitly; anyone else needs a grant from the Alerts → Capabilities tab.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const employees = (employeesQuery.data?.data ?? []).filter((e) => e.membershipId !== null);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="typo-h1 flex items-center gap-2">
          <SearchCheck className="size-6 text-kw-cerulean-dark" /> Investigations
        </h1>
        <p className="mt-1 text-[14px] text-kw-muted">
          How nonconformances are investigated in this organization. Rigor settings apply to records opened
          after you save — a record is always judged by the rules in force when it was opened.
        </p>
      </div>

      <Tabs
        tabs={TABS}
        activeTab={tab}
        onChange={(id) => {
          setTab(id as Tab);
        }}
      />

      {configQuery.isLoading ? <Card className="p-5 text-kw-muted">Loading…</Card> : null}
      {configQuery.data && tab === 'rules' ? <RulesTab config={configQuery.data} /> : null}
      {configQuery.data && tab === 'routing' ? (
        <RoutingTab config={configQuery.data} employees={employees} />
      ) : null}
      {tab === 'taxonomy' ? <TaxonomyTab /> : null}
    </div>
  );
}

// ── Rules ───────────────────────────────────────────────────────────────

function useSaveConfig() {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation({
    mutationFn: updateInvestigationConfig,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['investigations', 'config'] });
      show('Investigation settings saved.', { variant: 'success' });
    },
    onError: () => {
      show('Could not save the settings.', { variant: 'danger' });
    },
  });
}

function RulesTab({ config }: { config: InvestigationConfig }) {
  const [toggles, setToggles] = useState<Record<RigorKey, boolean>>(() => pickToggles(config));
  const [actions, setActions] = useState(config.resolutionActionOptions.join(', '));
  const save = useSaveConfig();

  // Re-hydrate when a save (or another tab's save) refreshes the config.
  useEffect(() => {
    setToggles(pickToggles(config));
    setActions(config.resolutionActionOptions.join(', '));
  }, [config]);

  const actionList = actions
    .split(',')
    .map((s) =>
      s
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_'),
    )
    .filter((s) => s.length > 0);

  return (
    <Card className="space-y-5 p-5">
      <div className="space-y-3">
        {RIGOR_TOGGLES.map((t) => (
          <label key={t.key} className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={toggles[t.key]}
              onChange={(e) => {
                setToggles((prev) => ({ ...prev, [t.key]: e.target.checked }));
              }}
            />
            <span>
              <span className="block text-[14px] font-medium text-kw-shark">{t.label}</span>
              <span className="block text-[12px] text-kw-muted">{t.description}</span>
            </span>
          </label>
        ))}
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-medium text-kw-muted">Resolution actions (comma-separated)</span>
        <Input
          value={actions}
          onChange={(e) => {
            setActions(e.target.value);
          }}
          placeholder="reship, replace, refund, credit, recount, no_action"
        />
        <span className="text-[11px] text-kw-ghost">
          The pick list on every resolution. Stored as slugs: {actionList.join(', ') || '—'}
        </span>
      </label>

      <div className="flex justify-end">
        <Button
          type="button"
          isLoading={save.isPending}
          disabled={save.isPending || actionList.length === 0}
          onClick={() => {
            save.mutate({ ...toggles, resolutionActionOptions: actionList });
          }}
        >
          <Check className="size-4" /> Save rules
        </Button>
      </div>
    </Card>
  );
}

function pickToggles(config: InvestigationConfig): Record<RigorKey, boolean> {
  return {
    requireQualityReview: config.requireQualityReview,
    requireSignedClosure: config.requireSignedClosure,
    requireReasonForChange: config.requireReasonForChange,
    requireStationAttribution: config.requireStationAttribution,
    requireCorrectiveAction: config.requireCorrectiveAction,
    requireInconclusiveReason: config.requireInconclusiveReason,
    requireIndependentReviewer: config.requireIndependentReviewer,
    allowPersonAttribution: config.allowPersonAttribution,
  };
}

// ── Routing ─────────────────────────────────────────────────────────────

function RoutingTab({ config, employees }: { config: InvestigationConfig; employees: EmployeeListItem[] }) {
  const [strategy, setStrategy] = useState<EscalationStrategy>(config.escalationStrategy);
  const [pool, setPool] = useState<string[]>(config.investigatorPoolMembershipIds);
  const [reviewer, setReviewer] = useState<ChainStep>(config.qualityReviewer);
  const [notify, setNotify] = useState<ChainStep>(config.unassignedNotify);
  const [search, setSearch] = useState('');
  const save = useSaveConfig();

  useEffect(() => {
    setStrategy(config.escalationStrategy);
    setPool(config.investigatorPoolMembershipIds);
    setReviewer(config.qualityReviewer);
    setNotify(config.unassignedNotify);
  }, [config]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      if (q === '') return e.isActive || pool.includes(e.membershipId ?? '');
      return `${e.firstName} ${e.lastName} ${e.email}`.toLowerCase().includes(q);
    });
  }, [employees, search, pool]);

  return (
    <Card className="space-y-6 p-5">
      <section className="space-y-2">
        <h2 className="text-[15px] font-semibold text-kw-shark">Investigator pool</h2>
        <p className="text-[13px] text-kw-muted">
          Initiators resolve their own records by default. When one is escalated, it goes to a member of this
          pool — nobody is routed to by name unless they are here.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-[13px] text-kw-shark">
            Strategy
            <select
              className={SELECT_CLASS}
              value={strategy}
              onChange={(e) => {
                setStrategy(e.target.value as EscalationStrategy);
              }}
            >
              <option value="least_loaded">Least loaded</option>
              <option value="round_robin">Round robin</option>
            </select>
          </label>
          <Input
            className="max-w-xs"
            placeholder="Search people"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
          <span className="text-[12px] text-kw-muted">{pool.length} in pool</span>
        </div>
        <ul className="max-h-64 divide-y divide-kw-border overflow-y-auto rounded-kw-md border border-kw-border">
          {visible.map((e) => {
            const mid = e.membershipId ?? '';
            const inPool = pool.includes(mid);
            return (
              <li key={e.id}>
                <label className="flex items-center gap-3 px-3 py-2 text-[13px] text-kw-shark">
                  <input
                    type="checkbox"
                    checked={inPool}
                    onChange={(ev) => {
                      setPool((prev) => (ev.target.checked ? [...prev, mid] : prev.filter((m) => m !== mid)));
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {e.firstName} {e.lastName}
                    <span className="ml-2 text-[12px] text-kw-muted">{e.jobTitle ?? e.email}</span>
                  </span>
                  {!e.isActive ? <Badge variant="neutral">inactive</Badge> : null}
                </label>
              </li>
            );
          })}
          {visible.length === 0 ? (
            <li className="px-3 py-2 text-[13px] text-kw-muted">No one matches.</li>
          ) : null}
        </ul>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StepSelector
          label="Quality reviewer"
          hint="Who accepts or returns a resolved record when quality review is on."
          value={reviewer}
          onChange={setReviewer}
          employees={employees}
        />
        <StepSelector
          label="Unassigned records go to"
          hint="Who is told when a record is handed off but the pool produced nobody."
          value={notify}
          onChange={setNotify}
          employees={employees}
        />
      </section>

      <div className="flex justify-end">
        <Button
          type="button"
          isLoading={save.isPending}
          disabled={save.isPending}
          onClick={() => {
            save.mutate({
              escalationStrategy: strategy,
              investigatorPoolMembershipIds: pool,
              qualityReviewer: reviewer,
              unassignedNotify: notify,
            });
          }}
        >
          <Check className="size-4" /> Save routing
        </Button>
      </div>
    </Card>
  );
}

/** One ADR-041 chain-step selector: lead | integrator | role | membership. */
function StepSelector({
  label,
  hint,
  value,
  onChange,
  employees,
}: {
  label: string;
  hint: string;
  value: ChainStep;
  onChange: (next: ChainStep) => void;
  employees: EmployeeListItem[];
}) {
  return (
    <div className="space-y-1.5">
      <span className="block text-[12px] font-medium text-kw-muted">{label}</span>
      <select
        className={`${SELECT_CLASS} w-full`}
        value={value.kind}
        onChange={(e) => {
          const kind = e.target.value;
          if (kind === 'lead' || kind === 'integrator') onChange({ kind });
          else if (kind === 'role') onChange({ kind: 'role', role: 'quality_lead' });
          else onChange({ kind: 'membership', membershipId: employees[0]?.membershipId ?? '' });
        }}
      >
        <option value="lead">The initiator’s lead</option>
        <option value="integrator">The integrator</option>
        <option value="role">Anyone with a role…</option>
        <option value="membership">A named person…</option>
      </select>
      {value.kind === 'role' ? (
        <select
          className={`${SELECT_CLASS} w-full`}
          value={value.role}
          onChange={(e) => {
            onChange({ kind: 'role', role: e.target.value });
          }}
        >
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      ) : null}
      {value.kind === 'membership' ? (
        <select
          className={`${SELECT_CLASS} w-full`}
          value={value.membershipId}
          onChange={(e) => {
            onChange({ kind: 'membership', membershipId: e.target.value });
          }}
        >
          {employees.map((e) => (
            <option key={e.id} value={e.membershipId ?? ''}>
              {e.firstName} {e.lastName}
            </option>
          ))}
        </select>
      ) : null}
      <span className="block text-[11px] text-kw-ghost">{hint}</span>
    </div>
  );
}

// ── Taxonomy ────────────────────────────────────────────────────────────

function TaxonomyTab() {
  const qc = useQueryClient();
  const { show } = useToast();
  const taxonomyQuery = useQuery({
    queryKey: ['investigations', 'taxonomy', 'all'],
    queryFn: () => listInvestigationTaxonomy({ includeInactive: true }),
  });

  function invalidate(): Promise<void> {
    return qc.invalidateQueries({ queryKey: ['investigations', 'taxonomy'] });
  }

  const starter = useMutation({
    mutationFn: addStarterTaxonomy,
    onSuccess: async (res) => {
      await invalidate();
      show(`Added ${String(res.created)} starter item(s).`, { variant: 'success' });
    },
    onError: () => {
      show('Could not add the starter set.', { variant: 'danger' });
    },
  });

  const bundle = taxonomyQuery.data;
  const total = bundle
    ? bundle.issueTypes.length +
      bundle.causes.length +
      bundle.stations.length +
      bundle.inconclusiveReasons.length
    : 0;

  if (!bundle) return <Card className="p-5 text-kw-muted">Loading…</Card>;

  return (
    <div className="space-y-4">
      {total === 0 ? (
        <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
          <p className="text-[13px] text-kw-muted">
            Nothing here yet. Add a generic starter set to begin, then rename or retire to fit.
          </p>
          <Button
            type="button"
            size="sm"
            isLoading={starter.isPending}
            onClick={() => {
              starter.mutate();
            }}
          >
            <Sparkles className="size-4" /> Add starter set
          </Button>
        </Card>
      ) : null}
      {TAXONOMY_SECTIONS.map((s) => (
        <TaxonomySection
          key={s.kind}
          kind={s.kind}
          title={s.title}
          hint={s.hint}
          items={bundle[s.bundleKey]}
          onChanged={invalidate}
        />
      ))}
    </div>
  );
}

function TaxonomySection({
  kind,
  title,
  hint,
  items,
  onChanged,
}: {
  kind: TaxonomyKind;
  title: string;
  hint: string;
  items: TaxonomyItem[];
  onChanged: () => Promise<void>;
}) {
  const { show } = useToast();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const isCause = kind === 'causes';

  const create = useMutation({
    mutationFn: () =>
      createTaxonomyItem(kind, {
        name: name.trim(),
        ...(isCause && category.trim() !== '' ? { category: category.trim().toLowerCase() } : {}),
        sortOrder: items.length,
      }),
    onSuccess: async () => {
      setName('');
      await onChanged();
    },
    onError: () => {
      show('Could not add that item — the name may already exist.', { variant: 'danger' });
    },
  });

  const update = useMutation({
    mutationFn: (args: { id: string; active?: boolean; name?: string }) =>
      updateTaxonomyItem(kind, args.id, {
        ...(args.active !== undefined ? { active: args.active } : {}),
        ...(args.name !== undefined ? { name: args.name } : {}),
      }),
    onSuccess: async () => {
      await onChanged();
    },
    onError: () => {
      show('Could not update that item.', { variant: 'danger' });
    },
  });

  const visible = items.filter((i) => showRetired || i.active);
  const retiredCount = items.filter((i) => !i.active).length;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim() !== '' && !create.isPending) create.mutate();
  }

  return (
    <Card className="space-y-3 p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-[15px] font-semibold text-kw-shark">{title}</h2>
          <p className="text-[12px] text-kw-muted">{hint}</p>
        </div>
        {retiredCount > 0 ? (
          <button
            type="button"
            className="text-[12px] text-kw-muted hover:text-kw-shark"
            onClick={() => {
              setShowRetired((v) => !v);
            }}
          >
            {showRetired ? 'Hide' : 'Show'} {retiredCount} retired
          </button>
        ) : null}
      </div>

      <ul className="divide-y divide-kw-border">
        {visible.map((item) => (
          <li key={item.id} className="flex items-center gap-2 py-1.5">
            <EditableName
              value={item.name}
              disabled={!item.active || update.isPending}
              onSave={(next) => {
                update.mutate({ id: item.id, name: next });
              }}
            />
            {isCause && item.category ? <Badge variant="info">{item.category}</Badge> : null}
            {!item.active ? <Badge variant="neutral">retired</Badge> : null}
            <button
              type="button"
              aria-label={item.active ? `Retire ${item.name}` : `Restore ${item.name}`}
              className="ml-auto text-kw-muted hover:text-kw-shark"
              disabled={update.isPending}
              onClick={() => {
                update.mutate({ id: item.id, active: !item.active });
              }}
            >
              {item.active ? <Archive className="size-4" /> : <RotateCcw className="size-4" />}
            </button>
          </li>
        ))}
        {visible.length === 0 ? <li className="py-1.5 text-[13px] text-kw-muted">None yet.</li> : null}
      </ul>

      <form className="flex flex-wrap items-end gap-2" onSubmit={submit}>
        <Input
          className="min-w-[12rem] flex-1"
          placeholder={`New ${title.toLowerCase().replace(/s$/, '')}`}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        {isCause ? (
          <Input
            className="max-w-[10rem]"
            placeholder="Category"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
            }}
          />
        ) : null}
        <Button
          type="submit"
          size="sm"
          variant="ghost"
          isLoading={create.isPending}
          disabled={name.trim() === ''}
        >
          <Plus className="size-4" /> Add
        </Button>
      </form>
    </Card>
  );
}

/** Click to rename; Enter or blur saves, Escape cancels. */
function EditableName({
  value,
  disabled,
  onSave,
}: {
  value: string;
  disabled: boolean;
  onSave: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  function commit(): void {
    setEditing(false);
    const next = draft.trim();
    if (next !== '' && next !== value) onSave(next);
    else setDraft(value);
  }

  if (!editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        className="text-left text-[13px] text-kw-shark hover:underline disabled:no-underline"
        onClick={() => {
          setEditing(true);
        }}
      >
        {value}
      </button>
    );
  }
  return (
    <input
      autoFocus
      className={SELECT_CLASS}
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') {
          setDraft(value);
          setEditing(false);
        }
      }}
    />
  );
}
